import Card from "components/card/Card";
import Page from "components/page/Page";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { ButtonNew as Button } from "components/buttonNew";
import "../loyalty.scss";
import DataTable from "components/dataTable/DataTable";
import { IReward } from "redux/reducers/models/loyalty";
import axios, { CancelToken } from "axios";
import { DeleteLoyaltyProgramReward, GetLoyaltyProgramRewards, PostDuplicateLoyaltyProgramReward, PostLoyaltyProgramStep3 } from "api/rpc/2024-04/facilityAdmin/customer/loyalty";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { displayCurrency } from "helpers/Helpers";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import useModal from "hooks/modals/useModal";
import Popup from "components/popup/Popup";
import LoyaltyProgramRewardsModal from "./LoyaltyProgramRewardsModal";

interface IState {
    rewards: Array<IReward>;
}


export default function LoyaltyProgramPointsStep3() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const [state, setState] = useState<IState>({
        rewards: null,
    });
    const { state: rewardModal, updateModal: updateRewardModal, closeModal: closeRewardModal } = useModal<{reward: IReward}>({reward: null});
    const { state: deleteModal, updateModal: updateDeleteModal, closeModal: closeDeleteModal } = useModal<{reward: IReward}>({reward: null});
    const { state: duplicateModal, updateModal: updateDuplicateModal, closeModal: closeDuplicateModal } = useModal<{reward: IReward}>({reward: null});

    const {facilityStore} = useAppSelector(store => store);
    
    useEffect(() => {
        const source = axios.CancelToken.source();
        void getRewards(source.token);
        return () => source.cancel();
    }, []);

    async function getRewards(cancelToken?: CancelToken) {
        const res = await GetLoyaltyProgramRewards(true, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting rewards")));
            return;
        }
        setState(prev => ({...prev, rewards: res?.data}));
    }


    function getTypeText(type: string, pointsRequired: number, discountValue: number) {
        switch (type){ 
            case "discount_amount":
                return `${pointsRequired} points = ${displayCurrency(facilityStore?.facility?.default_currency_code, discountValue)} off`;
            case "discount_percent":
                return `${pointsRequired} points = ${discountValue}% off`;
            case "free_product":
                return `${pointsRequired} points = Free Product`;
            case "birthday_bonus": 
                return t("Birthday bonus");
            default:
                return t("");
        }
    }

    async function handleAddReward(reward: Partial<IReward>) {
        if (!reward?.name) {
            dispatch(showError(t("Name required")));
            return;
        }
        
        const res = await PostLoyaltyProgramStep3({rewards: [reward]}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("Error updating reward")));
            return;
        }
        dispatch(showSuccess(t("Successfully added reward")));
        //refresh rewards
        await getRewards();
        void closeRewardModal();
    }

    async function handleDeleteReward() {
        if (deleteModal?.reward) {
            const res = await DeleteLoyaltyProgramReward({id: deleteModal?.reward?.id}, true);
            if (res?.status !== StatusCode.OK) {
                dispatch(showError(t("Error deleting reward")));
                return;
            }
            dispatch(showSuccess(t("Successfully deleted reward")));
            //refresh rewards
            await getRewards();
            void closeDeleteModal();
        }
    }

    async function handleDuplicateReward() {
        if (duplicateModal?.reward) {
            const res = await PostDuplicateLoyaltyProgramReward({id: duplicateModal?.reward?.id}, true);
            if (res?.status !== StatusCode.OK) {
                dispatch(showError(t("Error duplicating reward")));
                return;
            }
            dispatch(showSuccess(t("Successfully duplicated reward")));
            //refresh rewards
            await getRewards();
            void closeDuplicateModal();
        }
    }

    function navigateToNextStep() {
        if (state?.rewards?.length === 0) {
            dispatch(showError(t("At least one reward required before continuing to the next step")));
            return;
        }
        history.push("/admin/settings/loyalty-program");
    }

    return (
        <Page
        narrow
        title={t("Step 3: Create Reward Options")}
        breadcrumbs={[
            {
              prefix: true,
              label: t("Back"),
              url: "/admin/settings/loyalty-program/points/step-2",
            },
          ]}
        >
        <Card title={t("Rewards")} subtitle={t("Choose what customers can redeem their points for")} titleActions={[{content: "+ Create Reward", buttonType: "secondary", action: () => updateRewardModal({isOpen: true})}]}>
            <Card.Section>
            <br />
            <div className="loyalty-data-table">
            <DataTable
                columns={[{ label: t("How to redeem points") }, { label: "", width: "50%" }, { label: "" }]}
                className="min-h-auto"
              >
                {state?.rewards?.map((reward, index) => {
                    return (
                        <tr key={index}>
                            <td>
                                <p style={{color: "#101828"}}>{reward?.name}</p>
                                <p>{getTypeText(reward?.reward_type, reward?.points_required, reward?.discount_value)}</p>
                            </td>
                            <td></td>

                            <td>
                            <NavigationDropdownNew
                          showPlainTextLabel
                          rightAlign
                          label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                          sections={[
                            [
                              {
                                label: t("Edit"),
                                icon: "pen-to-square",
                                onClick: () => updateRewardModal({isOpen: true, reward: reward}),
                              },
                              {
                                label: t("Duplicate"),
                                icon: "copy",
                                onClick: () => updateDuplicateModal({isOpen: true, reward: reward}),
                              },
                              {
                                label: t("Delete"),
                                icon: "trash-can",
                                onClick: () => updateDeleteModal({isOpen: true, reward: reward}),
                              },
                            ],
                          ]}
                        />
                            </td>
                        </tr>
                    );
                })}
            </DataTable>
            </div>
            </Card.Section>
        </Card>
        <div className="loyalty-button-container">
        <Button type="primary" size="medium" onClick={() => navigateToNextStep()}>{t("Done")}</Button>
        </div>

        <LoyaltyProgramRewardsModal open={rewardModal?.isOpen} onCancel={closeRewardModal} onOk={reward => handleAddReward(reward)} reward={rewardModal?.reward}/>
        <Popup title={t("Delete Reward")} type="warning" description={`${t("Are you sure you want to delete reward")}: ${deleteModal?.reward?.name}?`} open={deleteModal?.isOpen} onCancel={closeDeleteModal} onOk={handleDeleteReward} okText={t("Delete")} />
        <Popup title={t("Duplicate Reward")} type="info" description={`${t("Are you sure you want to duplicate reward")}: ${duplicateModal?.reward?.name}?`} open={duplicateModal?.isOpen} onCancel={closeDuplicateModal} onOk={handleDuplicateReward} okText={t("Duplicate")} />

        </Page>
    );
}